import React, { useCallback, useEffect, useRef, useState } from 'react';

const API = '/api/api_eod_participant.php';
const CTS = ['CLIENT', 'FII', 'PRO', 'DII'];
const CL = { CLIENT: 'Client', FII: 'FII', PRO: 'Pro', DII: 'DII' };
const SG = [
  { k: 'FUT', l: 'Fut', isPut: false },
  { k: 'CE', l: 'CE', isPut: false },
  { k: 'PE', l: 'PE', isPut: true },
];
const MN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DW = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function fmt(n) {
  if (n == null || Number.isNaN(n)) return '—';
  const s = n < 0 ? '-' : '';
  const x = Math.abs(Number(n));
  if (x >= 1e7) return s + (x / 1e7).toFixed(2) + 'Cr';
  if (x >= 1e5) return s + (x / 1e5).toFixed(2) + 'L';
  if (x >= 1e3) return s + (x / 1e3).toFixed(2) + 'K';
  return s + x.toFixed(0);
}

function fD(d) {
  if (!d) return '—';
  const t = new Date(d + 'T00:00:00');
  const wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][t.getDay()];
  const mm = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][t.getMonth()];
  return `${wd}, ${t.getDate()} ${mm} ${t.getFullYear()}`;
}

function valClass(v, isPut) {
  if (v == null || Number.isNaN(v) || v === 0) return 'vneu';
  if (isPut) return v > 0 ? 'vneg' : 'vpos';
  return v > 0 ? 'vpos' : 'vneg';
}

function heroArrow(v, isPut) {
  if (v == null || Number.isNaN(v) || v === 0) return <span className="ec-arrow vneu">●</span>;
  const pos = isPut ? v < 0 : v > 0;
  return pos ? <span className="ec-arrow vpos">▲</span> : <span className="ec-arrow vneg">▼</span>;
}

function footArr(v, isPut) {
  if (v == null || Number.isNaN(v) || v === 0) return <span className="f-arr flat">—</span>;
  if (isPut) return v < 0 ? <span className="f-arr up">▲</span> : <span className="f-arr dn">▼</span>;
  return v > 0 ? <span className="f-arr up">▲</span> : <span className="f-arr dn">▼</span>;
}

function labelColor(label, isPut) {
  const l = label.toLowerCase();
  const isAddedLong = l.includes('added') && l.includes('long');
  const isClosedShort = l.includes('closed') && l.includes('short');
  const bullish = isAddedLong || isClosedShort;
  if (isPut) return bullish ? 'vneg' : 'vpos';
  return bullish ? 'vpos' : 'vneg';
}

function labelIco(label, isPut) {
  const l = label.toLowerCase();
  const isAddedLong = l.includes('added') && l.includes('long');
  const isClosedShort = l.includes('closed') && l.includes('short');
  const bullish = isAddedLong || isClosedShort;
  const pos = isPut ? !bullish : bullish;
  return pos
    ? <span className="ep-chk"><i className="bi bi-check-circle-fill" /></span>
    : <span className="ep-crs"><i className="bi bi-x-circle-fill" /></span>;
}

function ParticipantCard({ ctLabel, scopeLabel, segLabel, o = {}, ag = 0, fag = 0 }) {
  const isPut = !!o.isPut;
  const dc = o.netDC ?? null;
  const net0 = o.net ?? 0;
  const net1 = o.netHist?.[1] ?? null;
  const net2 = o.netHist?.[2] ?? null;
  const pct = o.pct ?? '—';
  const cpct = o.contribPct ?? null;
  const dL = o.dLong ?? null;
  const dS = o.dShort ?? null;
  const lm = o.longMove ?? 'Long';
  const sm = o.shortMove ?? 'Short';
  const dl = o.netDCLabel ?? '—';
  const dlc = o.dLongContrib ?? null;
  const dsc = o.dShortContrib ?? null;

  let isBull;
  if (isPut) isBull = dc !== null && dc < 0;
  else isBull = dc === null || dc >= 0;

  const cls = isBull ? 'ec bull' : 'ec bear';
  const dcCls = valClass(dc, isPut);
  const n0Cls = valClass(net0, isPut);
  const n1Cls = valClass(net1, isPut);
  const n2Cls = valClass(net2, isPut);

  const isBought = dl.toLowerCase().includes('bought');
  const badgePositive = isPut ? !isBought : isBought;
  const badgeCls = badgePositive ? 'ec-badge bought' : 'ec-badge sold';
  const badgeIco = badgePositive
    ? <i className="bi bi-arrow-up-short" />
    : <i className="bi bi-arrow-down-short" />;

  let contribHtml = null;
  if (cpct !== null && cpct !== 0) {
    const contribPositive = isPut ? cpct < 0 : cpct > 0;
    const ccls = contribPositive ? 'cb' : 'cs';
    contribHtml = <span className={`ec-contrib ${ccls}`}>{cpct > 0 ? '+' : ''}{cpct}%</span>;
  } else if (cpct === 0) {
    contribHtml = <span className="ec-contrib cn">0%</span>;
  }

  const dotCls = isBull ? 'ec-dot gd' : 'ec-dot rd';

  let footBgCls = 'foot-neu';
  if (net0 != null && !Number.isNaN(net0) && net0 !== 0) {
    if (isPut) footBgCls = net0 > 0 ? 'foot-bear' : 'foot-bull';
    else footBgCls = net0 > 0 ? 'foot-bull' : 'foot-bear';
  }

  const dParts = [];
  if (dL !== null) {
    const lcHtml = dlc !== null && dlc !== 0
      ? <span className="d-cpct">({dlc > 0 ? '+' : ''}{dlc}%)</span>
      : null;
    dParts.push(
      <span className="d-seg" key="long">
        <span className="d-ico">{labelIco(lm, isPut)}</span>
        <span className="d-lbl">{lm}:</span>
        <span className={`d-val ${labelColor(lm, isPut)}`}>{fmt(dL)}</span>
        {lcHtml}
      </span>
    );
  }
  if (dS !== null) {
    const scHtml = dsc !== null && dsc !== 0
      ? <span className="d-cpct">({dsc > 0 ? '+' : ''}{dsc}%)</span>
      : null;
    dParts.push(
      <span className="d-seg" key="short">
        <span className="d-ico">{labelIco(sm, isPut)}</span>
        <span className="d-lbl">{sm}:</span>
        <span className={`d-val ${labelColor(sm, isPut)}`}>{fmt(dS)}</span>
        {scHtml}
      </span>
    );
  }

  return (
    <div className={cls} style={{ '--ag': ag.toFixed(2), '--fag': fag.toFixed(2) }}>
      <div className="ec-top">
        <div className="ec-who">
          <span className="ec-seg">{segLabel}</span>
          {ctLabel} · {scopeLabel}
        </div>
        <span className={badgeCls}>{badgeIco} {dl}</span>
      </div>
      <div className="ec-hero">
        <div className={`ec-val ${dcCls}`}>
          {heroArrow(dc, isPut)}
          {fmt(dc)}
          {contribHtml}
        </div>
        <span className={dotCls} />
      </div>
      {dParts.length > 0 && (
        <div className="ec-detail">
          {dParts.map((part, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="d-sep">|</span>}
              {part}
            </React.Fragment>
          ))}
        </div>
      )}
      <div className={`ec-foot ${footBgCls}`}>
        <span className="f-pct">{pct}%</span>
        <span className={`f-hv f-cur ${n0Cls}`}>{fmt(net0)}</span>
        <span className="f-pipe">|</span>
        <span className={`f-hv ${n1Cls}`}>{footArr(net1, isPut)} {fmt(net1)}</span>
        <span className="f-pipe">|</span>
        <span className={`f-hv ${n2Cls}`}>{footArr(net2, isPut)} {fmt(net2)}</span>
      </div>
    </div>
  );
}

function segmentRows(data, scopeKey) {
  return SG.map(seg => {
    const row = CTS.map(ct => {
      const obj = { ...(data?.clients?.[ct]?.[scopeKey]?.[seg.k] || {}) };
      obj.isPut = seg.isPut;
      return obj;
    });
    const dcVals = row.map(o => Math.abs(o.netDC ?? 0));
    const netVals = row.map(o => Math.abs(o.net ?? 0));
    const maxDC = Math.max(...dcVals, 1);
    const maxNet = Math.max(...netVals, 1);
    return { seg, row, dcVals, netVals, maxDC, maxNet };
  });
}

function DesktopGrid({ data, scopeKey, scopeLabel }) {
  const segs = segmentRows(data, scopeKey);
  const cards = [];
  segs.forEach(({ seg, row, dcVals, netVals, maxDC, maxNet }) => {
    CTS.forEach((ct, i) => {
      const ag = dcVals[i] / maxDC;
      const fag = netVals[i] / maxNet;
      cards.push(
        <ParticipantCard
          key={`${scopeKey}-${seg.k}-${ct}`}
          ctLabel={CL[ct]}
          scopeLabel={scopeLabel}
          segLabel={seg.l}
          o={row[i]}
          ag={ag}
          fag={fag}
        />
      );
    });
  });
  return <div className="ep-grid">{cards}</div>;
}

function MobileAccordion({ data, openCt, onToggle }) {
  return (
    <div className="ep-acc" aria-label="Participant sections">
      {CTS.map((ct, i) => {
        const open = ct === openCt;
        const idxCards = segmentRows(data, 'index').map(({ seg, row, dcVals, netVals, maxDC, maxNet }) => (
          <ParticipantCard
            key={`idx-${seg.k}`}
            ctLabel={CL[ct]}
            scopeLabel="Idx"
            segLabel={seg.l}
            o={row[i]}
            ag={dcVals[i] / maxDC}
            fag={netVals[i] / maxNet}
          />
        ));
        const stkCards = segmentRows(data, 'stock').map(({ seg, row, dcVals, netVals, maxDC, maxNet }) => (
          <ParticipantCard
            key={`stk-${seg.k}`}
            ctLabel={CL[ct]}
            scopeLabel="Stk"
            segLabel={seg.l}
            o={row[i]}
            ag={dcVals[i] / maxDC}
            fag={netVals[i] / maxNet}
          />
        ));
        return (
          <div key={ct} className={`ep-acc-item ${open ? 'is-open' : ''}`} data-ct={ct}>
            <button
              type="button"
              className="ep-acc-btn"
              aria-expanded={open ? 'true' : 'false'}
              onClick={() => onToggle(ct)}
            >
              <span className="ep-acc-left">
                <span className="ep-acc-chip">{CL[ct]}</span>
                <span>{CL[ct]} participant</span>
              </span>
              <i className="bi bi-chevron-down ep-acc-chevron" aria-hidden="true" />
            </button>
            <div className="ep-acc-body">
              <div className="ep-acc-scope">Indices · Fut / CE / PE</div>
              {idxCards}
              <div className="ep-acc-scope">Stocks · Fut / CE / PE</div>
              {stkCards}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ParticipantPage() {
  const [dates, setDates] = useState([]);
  const [dateSet, setDateSet] = useState(new Set());
  const [ci, setCi] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [showCal, setShowCal] = useState(false);
  const [calY, setCalY] = useState(new Date().getFullYear());
  const [calM, setCalM] = useState(new Date().getMonth());
  const [stamp, setStamp] = useState('—');
  const [openCt, setOpenCt] = useState('CLIENT');
  const dnRef = useRef(null);

  const currentDate = dates[ci] || '';

  const load = useCallback(async (d) => {
    if (!d) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}?action=data&date=${encodeURIComponent(d)}`, { credentials: 'include', cache: 'no-store' });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const json = await res.json();
      if (json?.error) throw new Error(json.error);
      setData(json);
      setStamp(fD(json.date || d));
    } catch (e) {
      setData(null);
      setError(String(e.message || e));
      setStamp('ERROR');
    } finally {
      setLoading(false);
      setReady(true);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}?action=dates`, { credentials: 'include', cache: 'no-store' });
        const d = await res.json();
        if (Array.isArray(d) && d.length > 0) {
          setDates(d);
          setDateSet(new Set(d));
          setCi(0);
          const t = new Date(d[0] + 'T00:00:00');
          setCalY(t.getFullYear());
          setCalM(t.getMonth());
        } else {
          setReady(true);
          setLoading(false);
        }
      } catch (e) {
        console.error(e);
        setReady(true);
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (currentDate) load(currentDate);
  }, [currentDate, load]);

  useEffect(() => {
    if (currentDate) {
      const t = new Date(currentDate + 'T00:00:00');
      setCalY(t.getFullYear());
      setCalM(t.getMonth());
    }
  }, [currentDate]);

  useEffect(() => {
    const main = document.querySelector('.tmr-main');
    if (main) main.classList.add('ep-pg');
    return () => { if (main) main.classList.remove('ep-pg'); };
  }, []);

  const goPrev = useCallback(() => { setCi(i => (i < dates.length - 1 ? i + 1 : i)); }, [dates.length]);
  const goNext = useCallback(() => { setCi(i => (i > 0 ? i - 1 : i)); }, []);
  const goLatest = useCallback(() => { setCi(0); setShowCal(false); }, []);
  const goTo = useCallback((ds) => {
    setCi(i => {
      const idx = dates.indexOf(ds);
      if (idx >= 0) return idx;
      for (let j = 0; j < dates.length; j++) {
        if (dates[j] <= ds) return j;
      }
      return i;
    });
    setShowCal(false);
  }, [dates]);

  useEffect(() => {
    const onClick = (e) => {
      if (dnRef.current && !dnRef.current.contains(e.target)) setShowCal(false);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
      else if (e.key === 'l' || e.key === 'L') { e.preventDefault(); goLatest(); }
      else if (e.key === 'Escape') setShowCal(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [goPrev, goNext, goLatest]);

  const renderCal = () => {
    const sel = currentDate;
    const today = new Date().toISOString().slice(0, 10);
    const firstDay = new Date(calY, calM, 1).getDay();
    const dim = new Date(calY, calM + 1, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(<div key={'e' + i} className="cd" />);
    for (let day = 1; day <= dim; day++) {
      const ds = `${calY}-${String(calM + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const av = dateSet.has(ds);
      let cls = 'cd';
      if (av) cls += ' av';
      if (ds === today) cls += ' td';
      if (ds === sel) cls += ' sl';
      days.push(
        <div key={day} className={cls} {...(av ? { onClick: () => goTo(ds) } : {})}>{day}</div>
      );
    }
    return (
      <>
        <div className="ep-cal-h">
          <button type="button" className="cn" onClick={(e) => { e.stopPropagation(); let m = calM - 1; let y = calY; if (m < 0) { m = 11; y--; } setCalY(y); setCalM(m); }}><i className="bi bi-chevron-left" /></button>
          <span className="ct">{MN[calM]} {calY}</span>
          <button type="button" className="cn" onClick={(e) => { e.stopPropagation(); let m = calM + 1; let y = calY; if (m > 11) { m = 0; y++; } setCalY(y); setCalM(m); }}><i className="bi bi-chevron-right" /></button>
        </div>
        <div className="ep-cal-g">
          {DW.map(d => <div key={d} className="cw">{d}</div>)}
          {days}
        </div>
        <div className="ep-cal-f">
          <button type="button" className="cl" onClick={(e) => { e.stopPropagation(); goTo(today); }}>Today</button>
          <button type="button" className="cl" onClick={(e) => { e.stopPropagation(); goLatest(); }}>Latest Data</button>
        </div>
      </>
    );
  };

  const navDisabled = loading || dates.length === 0;

  return (
    <>
      {loading && (
        <div className="ep-loading" style={{ display: 'flex' }}>
          <div className="ep-spin" /> Loading participant data…
        </div>
      )}

      {(ready || dates.length > 0) && (
        <div className="ep-toolbar" aria-label="Date controls">
          <div className="ep-sh">
            <div className="t">Participant Data (Client • FII • Pro • DII)</div>
            <div className="ep-controls">
              <div className="ep-dn" ref={dnRef}>
                <button type="button" className="nb" onClick={goPrev} disabled={navDisabled || ci >= dates.length - 1} title="Previous trading day (←)">
                  <i className="bi bi-chevron-left" />
                </button>
                <div className="nd" onClick={(e) => { e.stopPropagation(); setShowCal(!showCal); }} title="Click to open calendar">
                  <i className="bi bi-calendar3" />
                  <span>{currentDate ? fD(currentDate) : (dates.length ? 'Loading…' : 'No data')}</span>
                </div>
                <button type="button" className="nb" onClick={goNext} disabled={navDisabled || ci <= 0} title="Next trading day (→)">
                  <i className="bi bi-chevron-right" />
                </button>
                {showCal && <div className="ep-cal open">{renderCal()}</div>}
              </div>
              <button type="button" className="ep-btn" onClick={goLatest} disabled={navDisabled} title="Jump to latest (L)">
                <i className="bi bi-skip-forward-fill" /> Latest
              </button>
              <div className="ep-stamp" title="Selected day data">
                <i className="bi bi-clock-history" />
                <span>{stamp}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && ready && (
        <div className="ep-wrap">
          <div className="ep-sec">
            {error ? (
              <div style={{ padding: 12, color: 'var(--red)', fontWeight: 800 }}>Failed to load: {error}</div>
            ) : data ? (
              <>
                <div className="ep-sec-label ep-desktop-view">Indices · Fut / CE / PE</div>
                <div className="ep-desktop-view">
                  <div className="ep-cols">
                    {CTS.map(c => <div key={c} className="ep-cn">{CL[c]}</div>)}
                  </div>
                  <DesktopGrid data={data} scopeKey="index" scopeLabel="Idx" />
                  <div className="ep-divider">
                    <span className="ep-divider-label">Stocks Participant Data</span>
                  </div>
                  <DesktopGrid data={data} scopeKey="stock" scopeLabel="Stk" />
                </div>
                <MobileAccordion data={data} openCt={openCt} onToggle={setOpenCt} />
              </>
            ) : null}
          </div>
        </div>
      )}
    </>
  );
}